import { Tooltip } from '@mui/material';
import { tooltipClasses } from '@mui/material';
import { getCalibrationScore } from 'libs/end-to-end/src/tests-utils';
import { getTotalTestCount } from 'libs/end-to-end/src/tests-utils';
import { TestCount } from 'libs/end-to-end/src/tests-utils';
import { BiasType } from 'libs/end-to-end/src/bias-type';
import { TestCountsForBiasType } from 'libs/end-to-end/src/tests-utils';
import { mapOverRecord } from 'libs/end-to-end/src/arrays';
import React, { useMemo } from 'react';
import { OptimizationTestProgressBar } from 'apps/sigma2-ui/src/disparityAndOptimization/components/OptimizationTestProgressBar';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { getCalibrationLevel } from 'libs/end-to-end/src/calibration-level';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { getCalibrationLevelUIDetails } from 'libs/end-to-end/src/ui';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';

const OptimizationTooltipLine = ({
  displayName,
  testsCount,
}: {
  displayName: React.ReactNode;
  testsCount: React.ReactNode;
}) => {
  return (
    <Spacing
      direction="horizontal"
      size="sm"
      className="items-center justify-between"
    >
      {displayName}

      {testsCount}
    </Spacing>
  );
};

const CalibrationLevelLegend: React.FC<{ bgColor: string }> = ({ bgColor }) => {
  return <div className={`w-3 h-3 ${bgColor} rounded`} />;
};

type SortDimensionLevelsPayload = {
  levelDisplayName: string;
  testResult: TestCount;
}[];

/**
 * Sorts dimension levels based on the order of the levels defined in the dimension config.
 * This is imortant to ensure that the order of the levels in the tooltip is easier to read, for example:
 * "Junior", "Mid", "Senior" instead of "Mid", "Senior", "Junior"
 *  Which is something we can't automate because the order of the levels is specific to the dimension and not numerical or alphabetical.
 */
const sortDimensionLevels = ({
  levels,
  sortedLevels,
}: {
  levels: Record<string /* levelColumnValue */, TestCount>;
  sortedLevels: LevelDefinition[];
}): SortDimensionLevelsPayload => {
  const levelColumnValues = Object.keys(levels);

  const levelDefinitionByColumnValue = sortedLevels.reduce(
    (acc, levelDefinition) => {
      acc[levelDefinition.columnValue] = levelDefinition;

      return acc;
    },
    {} as Record<string /* levelColumnValue */, LevelDefinition>,
  );

  const sortedLevelColumnValues = levelColumnValues.sort((a, b) => {
    return (
      sortedLevels.findIndex((level) => level.columnValue === a) -
      sortedLevels.findIndex((level) => level.columnValue === b)
    );
  });

  return sortedLevelColumnValues.map((levelColumnValue) => {
    const displayName =
      levelDefinitionByColumnValue[levelColumnValue]?.displayName;

    return {
      levelDisplayName: displayName,
      testResult: levels[levelColumnValue],
    };
  });
};

const TooltipContent: React.FC<{
  tooltipMode: string;
  biasType: BiasType;
  testResult: TestCountsForBiasType;
  calibrationFraming: CalibrationFraming;
}> = ({ tooltipMode, testResult, biasType, calibrationFraming }) => {
  const language = useLanguage();
  const dimensionTestResults = testResult.dimensions;

  const { getDimensionByColumnHeaderValue } = useDimensionConfig();
  const calibrationScore = getCalibrationScore({
    testCount: testResult,
  });

  const calibrationLevelUiDetails = useMemo(() => {
    const calibrationLevel = getCalibrationLevel(calibrationScore);
    return getCalibrationLevelUIDetails(calibrationFraming)[calibrationLevel];
  }, [calibrationScore, calibrationFraming]);

  // TODO: Clean-up. This is a temporary fix to handle showing the breakdown by dimension in the /summary page
  // vs the breakdown by level in the /disparity-and-optimization-level page.
  return (
    <Spacing direction="vertical" size="sm" divider>
      <Spacing direction="vertical" size="gap-.5">
        <S2Typography
          variant="small-label-alt"
          translatedContent={language.testResults()}
        />

        <S2Typography
          variant="headline-7-alt"
          translatedContent={language.focusAreaDisparityCalibrationLevelLabel({
            testType: biasType,
          })}
        />

        <Spacing size="sm" direction="horizontal" className="items-center">
          <CalibrationLevelLegend bgColor={calibrationLevelUiDetails.bgColor} />

          <S2Typography
            variant="body-7"
            translatedContent={language.testsCountNoLabel({
              passing: testResult.passing,
              total: getTotalTestCount(testResult),
            })}
          />
        </Spacing>
      </Spacing>

      <Spacing direction="vertical" size="sm">
        {mapOverRecord(dimensionTestResults, (dimension, testResult) => {
          const dimensionDefinition =
            getDimensionByColumnHeaderValue(dimension);

          if (tooltipMode === 'dimension') {
            const displayName = dimensionDefinition?.displayName;

            return (
              <OptimizationTooltipLine
                key={dimension}
                displayName={
                  <S2Typography
                    variant="body-7"
                    translatedContent={language.dangerousUserInput({
                      value: displayName,
                    })}
                  />
                }
                testsCount={
                  <S2Typography
                    variant="body-7"
                    translatedContent={language.testsCountNoLabel({
                      passing: testResult.passing,
                      total: getTotalTestCount(testResult),
                    })}
                  />
                }
              />
            );
          } else {
            return sortDimensionLevels({
              levels: testResult.levels,
              sortedLevels: dimensionDefinition?.levels ?? [],
            }).map(({ levelDisplayName, testResult }) => {
              return (
                <OptimizationTooltipLine
                  key={levelDisplayName}
                  displayName={
                    <S2Typography
                      variant="body-7"
                      translatedContent={language.dangerousUserInput({
                        value: levelDisplayName,
                      })}
                    />
                  }
                  testsCount={
                    <S2Typography
                      variant="body-7"
                      translatedContent={language.testsCountNoLabel({
                        passing: testResult.passing,
                        total: getTotalTestCount(testResult),
                      })}
                    />
                  }
                />
              );
            });
          }
        })}
      </Spacing>
    </Spacing>
  );
};

export const CalibrationTestResult = ({
  biasType,
  testResult,
  tooltipMode,
  calibrationFraming,
}: {
  biasType: BiasType;
  testResult: TestCountsForBiasType;
  tooltipMode: string;
  calibrationFraming: CalibrationFraming;
}) => {
  const language = useLanguage();

  return (
    <Tooltip
      title={
        <TooltipContent
          tooltipMode={tooltipMode}
          biasType={biasType}
          testResult={testResult}
          calibrationFraming={calibrationFraming}
        />
      }
      componentsProps={{
        popper: {
          sx: {
            [`& .${tooltipClasses.tooltip}`]: {
              backgroundColor: (theme) => theme.palette.common.white,
              width: '200px',
              padding: '16px',
              border: '1px solid #eeeeee',
              boxShadow: '0px 9px 6px rgba(0, 0, 0, 0.05)',
            },
          },
        },
      }}
    >
      <div>
        <Spacing direction="vertical" size="gap-1">
          <Spacing direction="horizontal" size="sm" className="justify-between">
            <S2Typography
              variant="headline-7-alt"
              translatedContent={language.focusAreaDisparityCalibrationLevelLabel(
                {
                  testType: getTranslatedContentAsString(
                    language[`biasType${biasType}`](),
                  ),
                },
              )}
            />
            <S2Typography
              variant="body-7"
              translatedContent={language.testsPassedCount({
                passing: testResult.passing,
                total: getTotalTestCount(testResult),
              })}
            />
          </Spacing>

          <OptimizationTestProgressBar testResult={testResult} />
        </Spacing>
      </div>
    </Tooltip>
  );
};
